import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useIsMobile } from "./useIsMobile";
import { useSwipeDownToClose } from "./useSwipeDownToClose";

/**
 * Renders its children as a normal anchored popover on >=640px, and as a bottom "slide-up" sheet
 * on phones (<640px), matching the mobile Figma designs for Share / Source Sheet / My Dashboards.
 *
 * Why a portal on mobile: the dashboard navbar gets `backdrop-blur-xs` when stickied, and any
 * element with a backdrop-filter establishes a containing block for `position: fixed` descendants.
 * A sheet rendered inline inside the navbar would therefore anchor to the navbar box (mis-placed)
 * once the user scrolls. Portaling to the iframe body escapes that containing block so `fixed`
 * resolves against the viewport. On desktop we render inline (no portal) to preserve the existing
 * `absolute top-full`-anchored popover behavior exactly.
 *
 * The consumer keeps full ownership of its header/body/footer markup and its open/close lifecycle
 * (this component is only mounted while the modal is open, so mounting == opening).
 */
interface MobileBottomSheetProps {
  onClose: () => void;
  /**
   * The iframe document the dashboard renders into. Used for the mobile media query and as the
   * portal target. Falls back to the top-level document if omitted.
   */
  targetDocument?: Document;
  /** Classes shared by both modes (background, text color, flex layout, container padding). */
  className?: string;
  /** Desktop-only positioning/size/rounded/shadow classes (e.g. `absolute top-full right-0 w-[500px]`). */
  desktopClassName: string;
  /** Optional extra classes for the mobile sheet container. */
  mobileClassName?: string;
  role?: string;
  ariaLabel?: string;
  /** Attached to the actual rendered container in BOTH modes so navbar click-outside detection
   * (which calls `ref.current.contains(target)`) keeps working through the portal. */
  containerRef?: React.Ref<HTMLDivElement>;
  /** Applied to the container in both modes (e.g. to stop propagation of inside clicks). */
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  children: React.ReactNode;
}

export const MobileBottomSheet: React.FC<MobileBottomSheetProps> = ({
  onClose,
  targetDocument,
  className = "",
  desktopClassName,
  mobileClassName = "",
  role = "dialog",
  ariaLabel,
  containerRef,
  onClick,
  children,
}) => {
  const doc = targetDocument ?? (typeof document !== "undefined" ? document : undefined);
  // `view` is still needed for requestAnimationFrame below; the breakpoint itself comes from the
  // shared hook rather than a second copy of the media query (SourceSheetModal passes its own
  // document's view the same way).
  const view = doc?.defaultView ?? null;
  const isMobile = useIsMobile(view);

  // Drives the enter transition (translate-y-full -> 0, backdrop fade in) on mount.
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!isMobile) {
      return;
    }
    const raf = view?.requestAnimationFrame(() => setEntered(true));
    return () => {
      if (raf != null) {
        view?.cancelAnimationFrame(raf);
      }
      setEntered(false);
    };
  }, [isMobile, view]);

  // Lock background scroll behind the sheet on mobile.
  useEffect(() => {
    if (!isMobile || !doc) {
      return;
    }
    const html = doc.documentElement;
    const prevOverflow = html.style.overflow;
    html.style.overflow = "hidden";
    return () => {
      html.style.overflow = prevOverflow;
    };
  }, [isMobile, doc]);

  // Swipe-down-to-close needs its own handle on the sheet element, but `containerRef` is a public
  // prop (navbar click-outside detection), so the mobile container gets a merged callback ref.
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const setSheetRef = useCallback(
    (node: HTMLDivElement | null) => {
      sheetRef.current = node;
      if (typeof containerRef === "function") {
        containerRef(node);
      } else if (containerRef) {
        (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [containerRef]
  );
  useSwipeDownToClose({ sheetRef, enabled: isMobile, onDismiss: onClose });

  if (!isMobile) {
    return (
      <div
        ref={containerRef}
        role={role}
        aria-label={ariaLabel}
        onClick={onClick}
        className={`${className} ${desktopClassName}`}
      >
        {children}
      </div>
    );
  }

  if (!doc?.body) {
    return null;
  }

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-[398] bg-black/40 transition-opacity duration-300 ${
          entered ? "opacity-100" : "opacity-0"
        }`}
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        ref={setSheetRef}
        role={role}
        aria-label={ariaLabel}
        onClick={onClick}
        className={`${className} fixed inset-x-0 bottom-0 z-[399] flex flex-col max-h-[85vh] rounded-t-2xl shadow-[0_-8px_40px_rgba(0,0,0,0.2)] transition-transform duration-300 ease-out ${
          entered ? "translate-y-0" : "translate-y-full"
        } ${mobileClassName}`}
      >
        {children}
      </div>
    </>,
    doc.body
  );
};
